/**
 * Lander Layout - 3D Flip Animation
 * 
 * WCAG 2.1 AA compliant flip animation interactions
 * Features: 3D flip effect, keyboard navigation, screen reader support, focus management
 * 
 * @package WordPress
 * @subpackage Lander_Layout
 * @version 1.0.0
 */

(function() {
    'use strict';
    
    // Configuration object
    const config = {
        selectors: {
            landerSection: '.lander',
            flipContainer: '.lander_flip_container',
            landerContent: '.lander_content',
            triggerButton: '.flip_trigger_button',
            closeButton: '.flip_close_button',
            frontContent: '.lander_content_front',
            backContent: '.lander_content_back',
        },
        classes: {
            flippedState: 'flipped',
        },
        attributes: {
            flipState: 'data-flip-state',
        },
        keys: {
            ENTER: 'Enter',
            SPACE: ' ',
            ESCAPE: 'Escape',
        },
        animation: {
            duration: 800, // matches CSS transition duration
        }
    };
    
    // State management
    const state = {
        isFlipped: false,
        isAnimating: false,
        lastFocusedElement: null,
    };
    
    // Utility functions
    const utils = {
        /**
         * Announce to screen readers
         */
        announce(message, priority = 'polite') {
            const announcer = document.createElement('div');
            announcer.setAttribute('aria-live', priority);
            announcer.setAttribute('aria-atomic', 'true');
            announcer.className = 'screen-reader-text';
            announcer.textContent = message;
            
            document.body.appendChild(announcer);
            
            setTimeout(() => {
                if (document.body.contains(announcer)) {
                    document.body.removeChild(announcer);
                }
            }, 1000);
        },
        
        /**
         * Check if element is focusable
         */
        isFocusable(element) {
            return element.tabIndex >= 0 && 
                   !element.disabled && 
                   !element.hidden &&
                   element.offsetParent !== null;
        },
        
        /**
         * Get first focusable element in container
         */
        getFirstFocusableElement(container) {
            const selectors = [
                'a[href]',
                'button:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ];
            
            const elements = container.querySelectorAll(selectors.join(','));
            return Array.from(elements).find(el => this.isFocusable(el)) || null;
        }
    };
    
    // Flip animation functionality
    const flipAnimation = {
        landerSection: null,
        flipContainer: null,
        landerContent: null,
        triggerButton: null,
        closeButton: null,
        frontContent: null,
        backContent: null,
        
        init() {
            this.landerSection = document.querySelector(config.selectors.landerSection);
            this.flipContainer = document.querySelector(config.selectors.flipContainer);
            this.landerContent = document.querySelector(config.selectors.landerContent);
            this.triggerButton = document.querySelector(config.selectors.triggerButton);
            this.closeButton = document.querySelector(config.selectors.closeButton);
            this.frontContent = document.querySelector(config.selectors.frontContent);
            this.backContent = document.querySelector(config.selectors.backContent);
            
            if (!this.landerSection || !this.flipContainer || !this.landerContent || !this.triggerButton || !this.closeButton) {
                return;
            }
            
            this.setupInitialState();
            this.bindEvents();
        },
        
        setupInitialState() {
            // Ensure initial state is set correctly
            this.flipContainer.setAttribute(config.attributes.flipState, 'front');
            this.landerContent.setAttribute(config.attributes.flipState, 'front');
            this.landerSection.setAttribute(config.attributes.flipState, 'front');
            
            // Set initial ARIA attributes
            if (this.triggerButton) {
                this.triggerButton.setAttribute('aria-expanded', 'false');
            }
            
            if (this.backContent) {
                this.backContent.setAttribute('aria-hidden', 'true');
            }
            
            if (this.frontContent) {
                this.frontContent.setAttribute('aria-hidden', 'false');
            }
        },
        
        bindEvents() {
            // Trigger button events
            if (this.triggerButton) {
                this.triggerButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flipToBack();
                });
                
                this.triggerButton.addEventListener('keydown', (e) => {
                    if (e.key === config.keys.ENTER || e.key === config.keys.SPACE) {
                        e.preventDefault();
                        this.flipToBack();
                    }
                });
            }
            
            // Close button events
            if (this.closeButton) {
                this.closeButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.flipToFront();
                });
                
                this.closeButton.addEventListener('keydown', (e) => {
                    if (e.key === config.keys.ENTER || e.key === config.keys.SPACE) {
                        e.preventDefault();
                        this.flipToFront();
                    }
                });
            }
            
            // Escape key to return to front
            document.addEventListener('keydown', (e) => {
                if (e.key === config.keys.ESCAPE && state.isFlipped && !state.isAnimating) {
                    this.flipToFront();
                }
            });
        },
        
        flipToBack() {
            if (state.isFlipped || state.isAnimating) return;
            
            state.isAnimating = true;
            state.lastFocusedElement = document.activeElement;
            
            // Update flip state
            this.flipContainer.setAttribute(config.attributes.flipState, 'back');
            this.landerContent.setAttribute(config.attributes.flipState, 'back');
            this.landerSection.setAttribute(config.attributes.flipState, 'back');
            
            // Update ARIA attributes
            if (this.triggerButton) {
                this.triggerButton.setAttribute('aria-expanded', 'true');
            }
            
            if (this.frontContent) {
                this.frontContent.setAttribute('aria-hidden', 'true');
            }
            
            if (this.backContent) {
                this.backContent.setAttribute('aria-hidden', 'false');
            }
            
            // Announce to screen readers
            utils.announce('Contact form opened');
            
            // Focus management after animation
            setTimeout(() => {
                state.isFlipped = true;
                state.isAnimating = false;
                
                // Focus the first focusable element in the back content
                const firstFocusable = utils.getFirstFocusableElement(this.backContent);
                if (firstFocusable) {
                    firstFocusable.focus();
                } else if (this.closeButton) {
                    this.closeButton.focus();
                }
            }, config.animation.duration);
        },
        
        flipToFront() {
            if (!state.isFlipped || state.isAnimating) return;
            
            state.isAnimating = true;
            
            // Update flip state
            this.flipContainer.setAttribute(config.attributes.flipState, 'front');
            this.landerContent.setAttribute(config.attributes.flipState, 'front');
            this.landerSection.setAttribute(config.attributes.flipState, 'front');
            
            // Update ARIA attributes
            if (this.triggerButton) {
                this.triggerButton.setAttribute('aria-expanded', 'false');
            }
            
            if (this.frontContent) {
                this.frontContent.setAttribute('aria-hidden', 'false');
            }
            
            if (this.backContent) {
                this.backContent.setAttribute('aria-hidden', 'true');
            }
            
            // Announce to screen readers
            utils.announce('Contact form closed');
            
            // Focus management after animation
            setTimeout(() => {
                state.isFlipped = false;
                state.isAnimating = false;
                
                // Return focus to the trigger button or last focused element
                if (state.lastFocusedElement && utils.isFocusable(state.lastFocusedElement)) {
                    state.lastFocusedElement.focus();
                } else if (this.triggerButton) {
                    this.triggerButton.focus();
                }
                
                state.lastFocusedElement = null;
            }, config.animation.duration);
        }
    };
    
    // Initialization
    const init = () => {
        // Check if we're in the admin or login page
        if (document.body.classList.contains('wp-admin') || 
            document.body.classList.contains('login')) {
            return;
        }
        
        // Only initialize if we're on a page with the lander layout
        if (!document.querySelector(config.selectors.landerSection)) {
            return;
        }
        
        flipAnimation.init();
    };
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
    
    // Export for debugging (only in development)
    if (window.location.hostname === 'localhost' || 
        window.location.hostname.includes('.local')) {
        window.landerFlip = {
            state,
            flipAnimation,
            utils,
            config
        };
    }
    
})();